<template>
  <div class="maintenance-policy-page">
    <header class="policy-heading">
      <div>
        <span class="eyebrow">{{ translate('Регламенты обслуживания', 'maintenance_regulations') }}</span>
        <h1>{{ translate('Настройка регламентных проверок и ТО', 'maintenance_policy_settings') }}</h1>
        <p>{{ translate('Виды обслуживания и периодичность по моделям оборудования и СИЗ', 'maintenance_policy_subtitle') }}</p>
      </div>
      <v-btn outlined color="primary" @click="openMaintenanceTypes">
        <v-icon left small>mdi-format-list-bulleted-type</v-icon>
        {{ translate('Виды технического обслуживания', 'maintenance_types') }}
      </v-btn>
    </header>

    <section class="policy-filters panel-card">
      <v-select
          v-model="category"
          :items="categoryItems"
          item-text="text"
          item-value="value"
          outlined dense hide-details
          :label="translate('Категория', 'category')"
      ></v-select>
      <v-text-field
          v-model="search"
          outlined dense clearable hide-details
          prepend-inner-icon="mdi-magnify"
          :label="translate('Поиск по модели или виду ТО', 'maintenance_policy_search')"
      ></v-text-field>
      <div class="filter-summary">
        <span>{{ translate('Моделей', 'models') }}</span>
        <strong>{{ filteredModels.length }}</strong>
      </div>
      <div class="filter-summary">
        <span>{{ translate('Регламентов', 'regulations') }}</span>
        <strong>{{ policies.length }}</strong>
      </div>
    </section>

    <div class="policy-workspace">
      <aside class="model-list panel-card">
        <div class="list-header">
          <div><span>{{ translate('Модель', 'model') }}</span><span>{{ translate('Правила', 'rules') }}</span></div>
        </div>
        <div class="model-scroll">
          <div v-if="loading" class="loading-state"><v-progress-circular indeterminate color="primary"></v-progress-circular></div>
          <button
              v-for="model in filteredModels"
              :key="model.id"
              type="button"
              class="model-row"
              :class="{'model-row--active': selectedModelId === model.id}"
              @click="selectModel(model.id)"
          >
            <span class="model-avatar"><v-icon>{{ isPpeModel(model) ? 'mdi-shield-check-outline' : 'mdi-gas-cylinder' }}</v-icon></span>
            <span class="model-copy"><strong>{{ modelTitle(model) }}</strong><small>{{ model.code || '—' }}</small></span>
            <span class="rule-count">{{ policyCount(model.id) }}</span>
          </button>
          <div v-if="!loading && !filteredModels.length" class="empty-state">{{ translate('Модели не найдены', 'models_not_found') }}</div>
        </div>
      </aside>

      <main class="policy-details panel-card">
        <template v-if="selectedModel">
          <header class="details-header">
            <div>
              <span class="eyebrow">{{ isPpeModel(selectedModel) ? translate('СИЗ', 'ppe') : translate('Оборудование', 'equipment') }}</span>
              <h2>{{ modelTitle(selectedModel) }}</h2>
              <p>{{ translate('Код модели', 'model_code') }}: {{ selectedModel.code || '—' }}</p>
            </div>
            <div class="details-actions">
              <span class="active-pill"><i></i>{{ translate('Выбрано', 'selected') }}: {{ selectedDraftCount }}</span>
              <v-btn color="primary" depressed :loading="saving" :disabled="!hasDraftChanges || saving" @click="savePolicies">
                <v-icon left small>mdi-content-save-outline</v-icon>{{ translate('Сохранить', 'save') }}
              </v-btn>
            </div>
          </header>

          <section class="policy-table-wrap">
            <div class="section-title">
              <div><span class="eyebrow">{{ translate('Правила', 'rules') }}</span><h3>{{ translate('Регламентные проверки и обслуживание', 'scheduled_checks') }}</h3></div>
            </div>
            <div class="policy-table-scroll">
              <table class="policy-table">
                <thead><tr>
                  <th class="check-column"><v-checkbox :input-value="allDraftsSelected" :indeterminate="someDraftsSelected" hide-details dense :disabled="!canAdd && !canDelete" @change="toggleAllPolicies"></v-checkbox></th>
                  <th class="type-column">{{ translate('Вид обслуживания', 'maintenance_type_ref_label') }}</th>
                  <th class="description-column">{{ translate('Описание', 'description') }}</th>
                  <th class="period-column">{{ translate('Периодичность', 'maintenancePeriod') }}</th>
                  <th class="status-column">{{ translate('Статус', 'status') }}</th>
                </tr></thead>
                <tbody>
                  <tr v-for="draft in policyDrafts" :key="draft.typeId" :class="{'policy-row--selected': draft.selected}">
                    <td class="check-column"><v-checkbox v-model="draft.selected" hide-details dense :disabled="draft.policyId ? !canDelete : !canAdd"></v-checkbox></td>
                    <td class="type-column"><strong>{{ typeTitle(draft.type) }}</strong><small class="type-code">{{ draft.type.code || '' }}</small></td>
                    <td class="description-column"><div class="description-text">{{ typeDescription(draft.type) || '—' }}</div></td>
                    <td class="period-column">
                      <v-text-field v-model.number="draft.period" type="number" min="1" step="1" outlined dense hide-details suffix="мес." :disabled="!draft.selected || (draft.policyId ? !canEdit : !canAdd)"></v-text-field>
                    </td>
                    <td class="status-column"><span class="row-status" :class="draft.selected ? 'row-status--active' : 'row-status--inactive'">{{ draft.selected ? translate('Выбрано', 'selected') : translate('Не используется', 'not_used') }}</span></td>
                  </tr>
                  <tr v-if="!policyDrafts.length"><td colspan="5" class="empty-table">
                    <v-icon>mdi-format-list-bulleted-type</v-icon>
                    <strong>{{ translate('В справочнике ещё нет видов ТО', 'no_maintenance_types') }}</strong>
                    <span>{{ translate('Сначала добавьте виды технического обслуживания', 'add_maintenance_types_hint') }}</span>
                  </td></tr>
                </tbody>
              </table>
            </div>
            <div class="table-save-bar">
              <span v-if="hasDraftChanges"><v-icon small>mdi-circle-medium</v-icon>{{ translate('Есть несохранённые изменения', 'unsaved_changes') }}</span>
              <span v-else>{{ translate('Все изменения сохранены', 'all_changes_saved') }}</span>
            </div>
          </section>

          <section class="policy-note">
            <v-icon>mdi-information-outline</v-icon>
            <span>{{ translate('Следующая дата обслуживания в карточке рассчитывается от даты последнего выполненного ТО с учётом периода правила.', 'maintenance_policy_calculation_hint') }}</span>
          </section>
        </template>
        <div v-else class="details-empty"><v-icon size="54">mdi-tune-variant</v-icon><span>{{ translate('Выберите модель слева', 'select_model_left') }}</span></div>
      </main>
    </div>
    <v-snackbar v-model="saveSuccess" color="success" :timeout="2500">{{ translate('Регламенты сохранены', 'regulations_saved') }}</v-snackbar>
  </div>
</template>

<script>
const getApi = () => import('@/services/Api');

export default {
  name: 'maintenance-policy-page',
  data: () => ({loading: false, saving: false, saveSuccess: false, models: [], policies: [], maintenanceTypes: [], policyDrafts: [], selectedModelId: null, search: '', category: 'all'}),
  computed: {
    chosenLang() { return String(this.$store.getters.getChosenLang || 'rus').toLowerCase(); },
    categoryItems() {
      return [
        {value: 'all', text: this.translate('Все категории', 'all_categories')},
        {value: 'equipment', text: this.translate('Оборудование', 'equipment')},
        {value: 'ppe', text: this.translate('СИЗ', 'ppe')},
      ];
    },
    filteredModels() {
      const query = String(this.search || '').trim().toLowerCase();
      return this.models.filter(model => {
        if (this.category === 'ppe' && !this.isPpeModel(model)) return false;
        if (this.category === 'equipment' && this.isPpeModel(model)) return false;
        if (!query) return true;
        const relatedPolicies = this.policies.filter(policy => Number((policy.modelType || {}).id) === Number(model.id));
        const haystack = [this.modelTitle(model), model.code]
          .concat(relatedPolicies.map(policy => `${policy.name || ''} ${this.typeTitle(policy.type)}`))
          .join(' ').toLowerCase();
        return haystack.includes(query);
      });
    },
    selectedModel() { return this.models.find(model => Number(model.id) === Number(this.selectedModelId)) || null; },
    selectedPolicies() {
      return this.policies
        .filter(policy => Number((policy.modelType || {}).id) === Number(this.selectedModelId))
        .sort((a, b) => this.typeTitle(a.type).localeCompare(this.typeTitle(b.type), 'ru'));
    },
    selectedDraftCount() { return this.policyDrafts.filter(draft => draft.selected).length; },
    allDraftsSelected() { return Boolean(this.policyDrafts.length) && this.policyDrafts.every(draft => draft.selected); },
    someDraftsSelected() { return this.selectedDraftCount > 0 && !this.allDraftsSelected; },
    hasDraftChanges() {
      return this.policyDrafts.some(draft => draft.selected !== draft.originalSelected || (draft.selected && Number(draft.period) !== Number(draft.originalPeriod)));
    },
    canAdd() { return this.$store.getters.isPerimitted('MaintenancePolicy', 'add'); },
    canEdit() { return this.$store.getters.isPerimitted('MaintenancePolicy', 'edit'); },
    canDelete() { return this.$store.getters.isPerimitted('MaintenancePolicy', 'delete'); },
  },
  watch: {
    chosenLang() { this.loadData(false); },
    filteredModels(models) {
      if (models.length && !models.some(model => model.id === this.selectedModelId)) this.selectModel(models[0].id);
    },
  },
  created() { this.loadData(); },
  methods: {
    translate(def, code, entity) { return this.$store.getters.getTranslation(def, code, entity); },
    localized(value) {
      if (!value) return '';
      if (typeof value === 'string') return value;
      const source = value.name || value;
      return source[this.chosenLang] || source.rus || source.eng || source.value || source.code || '';
    },
    modelTitle(model) { return this.localized(model.name) || model.code || `#${model.id}`; },
    typeTitle(type) { return this.localized(type && type.name) || this.localized(type) || '—'; },
    typeDescription(type) { return (type && type.description) || ''; },
    isPpeModel(model) { return Number(model && model.code) > 500000; },
    policyCount(modelId) { return this.policies.filter(policy => Number((policy.modelType || {}).id) === Number(modelId)).length; },
    validPeriod(period) { return Number.isInteger(Number(period)) && Number(period) > 0; },
    selectModel(modelId) {
      if (Number(this.selectedModelId) === Number(modelId)) return;
      this.selectedModelId = modelId;
      this.preparePolicyDrafts();
    },
    preparePolicyDrafts() {
      const policiesByType = new Map(this.selectedPolicies.map(policy => [Number((policy.type || {}).id), policy]));
      this.policyDrafts = this.maintenanceTypes
        .map(type => {
          const policy = policiesByType.get(Number(type.id));
          const period = policy ? Number(policy.maintenancePeriod) : 12;
          return {type, typeId: Number(type.id), policyId: policy && policy.id, selected: Boolean(policy), period, originalSelected: Boolean(policy), originalPeriod: period};
        })
        .sort((a, b) => this.typeTitle(a.type).localeCompare(this.typeTitle(b.type), 'ru'));
    },
    toggleAllPolicies(value) {
      this.policyDrafts.forEach(draft => {
        if ((draft.policyId && this.canDelete) || (!draft.policyId && this.canAdd)) draft.selected = Boolean(value);
      });
    },
    async loadAllRows(http, entity) {
      const rows = [];
      const rowsPerPage = 500;
      let page = 1;
      let totalRows = Number.POSITIVE_INFINITY;
      while (rows.length < totalRows) {
        const response = await http.getData(`view/${entity}`, {
          lang: this.chosenLang,
          sort: {descending: false, rowsPerPage, page},
          where: [],
        });
        const data = response.data || {};
        const pageRows = data.tabRows || [];
        rows.push(...pageRows);
        totalRows = Number(data.totalRows);
        if (!Number.isFinite(totalRows)) totalRows = rows.length;
        if (!pageRows.length || pageRows.length < rowsPerPage) break;
        page += 1;
      }
      return rows;
    },
    async loadData(showProgress = true) {
      this.loading = true;
      if (showProgress) await this.$store.dispatch('storeProgress', {text: 'downloading'});
      try {
        const {http} = await getApi();
        const [models, policies, maintenanceTypes] = await Promise.all([
          this.loadAllRows(http, 'AssetModel'),
          this.loadAllRows(http, 'MaintenancePolicy'),
          this.loadAllRows(http, 'AssetMaintenanceType'),
        ]);
        this.models = models.sort((a, b) => this.modelTitle(a).localeCompare(this.modelTitle(b), 'ru'));
        this.policies = policies;
        this.maintenanceTypes = maintenanceTypes;
        if (!this.selectedModelId && this.models.length) this.selectedModelId = this.models[0].id;
        this.preparePolicyDrafts();
      } finally {
        this.loading = false;
        if (showProgress) await this.$store.dispatch('storeProgress', {text: null});
      }
    },
    openMaintenanceTypes() { this.$router.push({name: 'Table', params: {tabName: 'AssetMaintenanceType'}}); },
    async savePolicies() {
      if (!this.selectedModel || !this.hasDraftChanges || this.saving) return;
      const invalid = this.policyDrafts.find(draft => draft.selected && !this.validPeriod(draft.period));
      if (invalid) return this.$store.dispatch('storeError', this.translate('Период проверки должен быть целым числом больше нуля', 'maintenance_period_invalid'));
      this.saving = true;
      await this.$store.dispatch('storeProgress', {text: this.translate('Сохранение', 'saving')});
      try {
        const {http} = await getApi();
        const requests = [];
        this.policyDrafts.forEach(draft => {
          if (!draft.originalSelected && draft.selected) {
            requests.push(http.putData('/catalogs/change/MaintenancePolicy', {params: {modelTypeId: this.selectedModel.id, typeId: draft.typeId, maintenancePeriod: Number(draft.period), name: this.typeTitle(draft.type)}}));
          } else if (draft.originalSelected && draft.selected && Number(draft.period) !== Number(draft.originalPeriod)) {
            requests.push(http.postData('/catalogs/change/MaintenancePolicy', {params: {id: draft.policyId, modelTypeId: this.selectedModel.id, typeId: draft.typeId, maintenancePeriod: Number(draft.period), name: this.typeTitle(draft.type)}}));
          } else if (draft.originalSelected && !draft.selected) {
            requests.push(http.deleteData(`/catalogs/MaintenancePolicy/${draft.policyId}`));
          }
        });
        await Promise.all(requests);
        await this.loadData(false);
        this.saveSuccess = true;
      } catch (error) {
        await this.loadData(false);
      } finally {
        this.saving = false;
        await this.$store.dispatch('storeProgress', {text: null});
      }
    },
  },
};
</script>

<style scoped>
.maintenance-policy-page { display: flex; height: calc(100vh - 56px); min-height: 0; padding: 18px; box-sizing: border-box; flex-direction: column; overflow: hidden; color: #18344d; background: #f5f8fa; }.panel-card { border: 1px solid #dce7ee; border-radius: 13px; background: #fff; box-shadow: 0 8px 24px rgba(31,65,93,.055); }.policy-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 18px; margin-bottom: 15px; flex: none; }.policy-heading h1 { margin: 3px 0 4px; color: #153148; font-size: 27px; }.policy-heading p,.details-header p { margin: 0; color: #768797; }.eyebrow { color: #07888a; font-size: 10px; font-weight: 800; letter-spacing: .13em; text-transform: uppercase; }
.policy-filters { display: grid; grid-template-columns: 260px minmax(320px,1fr) 120px 120px; align-items: center; gap: 12px; padding: 14px; flex: none; }.filter-summary { display: flex; min-height: 40px; padding: 4px 10px; border-left: 1px solid #e3ebf0; flex-direction: column; }.filter-summary span { color: #82909d; font-size: 10px; text-transform: uppercase; }.filter-summary strong { color: #07888a; font-size: 18px; }
.policy-workspace { display: grid; min-height: 0; grid-template-columns: minmax(310px,38%) minmax(0,62%); gap: 12px; margin-top: 12px; flex: 1; }.model-list,.policy-details { height: 100%; min-height: 0; overflow: hidden; }.model-list { display: flex; flex-direction: column; }.policy-details { overflow-y: auto; overscroll-behavior: contain; }.model-scroll { min-height: 0; flex: 1; overflow-y: auto; overscroll-behavior: contain; scrollbar-gutter: stable; }.list-header { flex: none; padding: 15px 18px 10px; border-bottom: 1px solid #e4ebf0; }.list-header div { display: flex; justify-content: space-between; color: #80909e; font-size: 11px; font-weight: 700; text-transform: uppercase; }.model-row { display: grid; width: 100%; grid-template-columns: 42px minmax(0,1fr) 34px; align-items: center; gap: 10px; padding: 12px 16px; border: 0; border-bottom: 1px solid #edf1f4; color: #29465d; text-align: left; background: #fff; cursor: pointer; transition: .15s ease; }.model-row:hover { background: #f4fbfb; }.model-row--active { box-shadow: inset 4px 0 #079b91; background: #eaf9f7; }.model-avatar { display: grid; place-items: center; width: 36px; height: 36px; border-radius: 10px; color: #07888a; background: #edf8f8; }.model-avatar .v-icon { color: inherit; }.model-copy { display: flex; min-width: 0; flex-direction: column; }.model-copy strong,.model-copy small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.model-copy strong { font-size: 13px; }.model-copy small { margin-top: 2px; color: #84929f; font-size: 10px; }.rule-count { display: grid; place-items: center; min-width: 28px; height: 28px; border-radius: 8px; color: #087f73; font-size: 12px; font-weight: 800; background: #e7f7f2; }.loading-state,.empty-state,.details-empty { display: flex; align-items: center; justify-content: center; min-height: 220px; color: #81909e; }.empty-state { font-size: 13px; }
.details-header { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 18px 20px; border-bottom: 1px solid #e4ebf0; }.details-header h2 { margin: 3px 0; font-size: 21px; }.details-actions { display: flex; align-items: center; gap: 10px; }.active-pill { display: inline-flex; align-items: center; gap: 6px; padding: 7px 10px; border-radius: 8px; color: #12805d; font-size: 11px; font-weight: 700; background: #eaf8f1; }.active-pill i { width: 7px; height: 7px; border-radius: 50%; background: #11aa75; }.policy-table-wrap { padding: 16px; }.section-title h3 { margin: 2px 0 12px; font-size: 17px; }.policy-table-scroll { max-height: calc(100vh - 445px); overflow: auto; border: 1px solid #e3eaf0; border-radius: 10px; }.policy-table { width: 100%; min-width: 760px; table-layout: fixed; border-collapse: collapse; }.policy-table thead { position: sticky; z-index: 1; top: 0; }.policy-table th { padding: 10px 12px; color: #6f8190; font-size: 10px; text-align: left; text-transform: uppercase; background: #f7f9fb; }.policy-table td { padding: 9px 12px; border-top: 1px solid #e7edf1; color: #526779; font-size: 12px; }.policy-table td:first-child { color: #29465d; }.policy-row--selected { background: #f7fcfb; }.check-column { width: 48px; }.check-column .v-input { margin: 0; }.type-column { width: 225px; overflow-wrap: anywhere; }.type-code { display: block; margin-top: 2px; color: #8997a2; font-size: 9px; }.description-column { width: 290px; max-width: 290px; overflow: hidden; }.description-text { display: block; width: 100%; max-width: 100%; overflow: hidden; line-height: 1.35; white-space: normal !important; overflow-wrap: anywhere; word-break: normal; }.period-column { position: relative; z-index: 1; width: 175px; }.period-column .v-input { width: 155px; max-width: 155px; border-radius: 4px; background: #fff; }.status-column { width: 130px; }.row-status { display: inline-flex; padding: 5px 8px; border-radius: 7px; font-size: 10px; font-weight: 700; white-space: nowrap; }.row-status--active { color: #12805d; background: #eaf8f1; }.row-status--inactive { color: #7c8994; background: #eef2f5; }.table-save-bar { display: flex; align-items: center; gap: 12px; padding-top: 14px; color: #7b8a96; font-size: 11px; }.table-save-bar span:first-child { display: inline-flex; align-items: center; }.table-save-bar span:first-child .v-icon { color: #e19b23; }.empty-table { height: 220px; text-align: center !important; }.empty-table .v-icon,.empty-table strong,.empty-table span { display: block; margin: 6px auto; }.empty-table .v-icon { color: #95a5b0; }.empty-table strong { color: #435d72; }.empty-table span { color: #81909d; }.policy-note { display: flex; align-items: flex-start; gap: 9px; margin: 0 16px 16px; padding: 12px; border-radius: 9px; color: #3c7180; font-size: 11px; background: #edf8fb; }.policy-note .v-icon { color: #168ba0; }.details-empty { flex-direction: column; gap: 12px; }.details-empty .v-icon { color: #9babb6; }
@media (max-width: 1100px) { .maintenance-policy-page { height: auto; min-height: calc(100vh - 56px); overflow: visible; }.policy-filters { grid-template-columns: 1fr 1fr; }.policy-workspace { grid-template-columns: 1fr; flex: none; }.model-list { height: 360px; min-height: 360px; }.policy-details { height: auto; min-height: 480px; overflow: visible; }.policy-table-scroll { max-height: 440px; } }
@media (max-width: 700px) { .policy-heading,.details-header { align-items: stretch; flex-direction: column; }.policy-filters { grid-template-columns: 1fr; }.filter-summary { border-left: 0; }.details-actions { align-items: stretch; flex-direction: column; } }
</style>
